import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridPinnedColumns,
} from '@mui/x-data-grid-pro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchTableConfig, UpsertTableConfig } from 'src/constants/actionTypes'
import { ColumnPreferences } from 'src/interfaces/tableConfig'
import { getUser } from 'src/selectors/user'

export interface GridColumnPreferences {
  columnVisibilityModel: GridColumnVisibilityModel
  pinnedColumns: GridPinnedColumns
  orderedFields: Array<string>
}

const STORAGE_KEY_PREFIX = 'grid-column-prefs:'
const DEBOUNCE_MS = 700

const buildStorageKey = (userId: string, tableKey: string) => {
  return `${STORAGE_KEY_PREFIX}${userId}:${tableKey}`
}

const loadFromStorage = (userId: string, tableKey: string): GridColumnPreferences | null => {
  try {
    const raw = localStorage.getItem(buildStorageKey(userId, tableKey))

    if (!raw) {
      return null
    }

    return JSON.parse(raw) as GridColumnPreferences
  } catch {
    return null
  }
}

const saveToStorage = (userId: string, tableKey: string, preferences: GridColumnPreferences) => {
  try {
    localStorage.setItem(buildStorageKey(userId, tableKey), JSON.stringify(preferences))
  } catch {}
}

export const clearAllGridColumnPreferences = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => {
        return key.startsWith(STORAGE_KEY_PREFIX)
      })
      .forEach((key) => {
        localStorage.removeItem(key)
      })
  } catch {}
}

const toApiPreferences = (prefs: GridColumnPreferences): ColumnPreferences => {
  return {
    columnVisibility: prefs.columnVisibilityModel as Record<string, boolean>,
    columnOrder: prefs.orderedFields,
    pinnedColumns: {
      left: prefs.pinnedColumns.left ?? [],
      right: prefs.pinnedColumns.right ?? [],
    },
  }
}

const fromApiPreferences = (
  prefs: ColumnPreferences,
  defaults: GridColumnPreferences,
): GridColumnPreferences => {
  return {
    columnVisibilityModel: prefs.columnVisibility ?? defaults.columnVisibilityModel,
    pinnedColumns: prefs.pinnedColumns ?? defaults.pinnedColumns,
    orderedFields: prefs.columnOrder ?? defaults.orderedFields,
  }
}

const mergeOrderedFields = (
  orderedFields: Array<string>,
  defaultOrderedFields: Array<string>,
): Array<string> => {
  if (!orderedFields.length) {
    return defaultOrderedFields
  }

  const storedSet = new Set(orderedFields)
  const newFields = defaultOrderedFields.filter((field) => {
    return !storedSet.has(field)
  })

  return [
    ...orderedFields.filter((field) => {
      return defaultOrderedFields.includes(field)
    }),
    ...newFields,
  ]
}

interface UseGridColumnPreferencesOptions {
  tableKey: string
  columns: Array<GridColDef>
  lockedColumns?: Array<string>
  defaultPinnedColumns?: GridPinnedColumns
}

export interface UseGridColumnPreferencesResult {
  columnVisibilityModel: GridColumnVisibilityModel
  pinnedColumns: GridPinnedColumns
  orderedFields: Array<string>
  orderedColumns: Array<GridColDef>
  onColumnVisibilityModelChange(model: GridColumnVisibilityModel): void
  onPinnedColumnsChange(pinned: GridPinnedColumns): void
  onColumnOrderChange(params: GridColumnOrderChangeParams): void
  onToggleColumnVisibility(field: string): void
  onPinColumn(field: string): void
  onUnpinColumn(field: string): void
}

export const useGridColumnPreferences = ({
  tableKey,
  columns,
  lockedColumns = [],
  defaultPinnedColumns = {},
}: UseGridColumnPreferencesOptions): UseGridColumnPreferencesResult => {
  const dispatch = useDispatch()
  const userId = useSelector(getUser)?.id ?? null
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const defaultVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    return columns.reduce<GridColumnVisibilityModel>((model, column) => {
      model[column.field] = true

      return model
    }, {})
  }, [columns])

  const defaultOrderedFields = useMemo(() => {
    return columns.map((column) => {
      return column.field
    })
  }, [columns])

  const defaultPreferences = useMemo<GridColumnPreferences>(
    () => ({
      columnVisibilityModel: defaultVisibilityModel,
      pinnedColumns: defaultPinnedColumns,
      orderedFields: defaultOrderedFields,
    }),
    [defaultVisibilityModel, defaultPinnedColumns, defaultOrderedFields],
  )

  const storedLocally = useMemo(() => {
    if (!userId) {
      return null
    }

    return loadFromStorage(userId, tableKey)
  }, [userId, tableKey])

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => ({ ...defaultVisibilityModel, ...(storedLocally?.columnVisibilityModel ?? {}) }),
  )

  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>(
    () => storedLocally?.pinnedColumns ?? defaultPinnedColumns,
  )

  const [orderedFields, setOrderedFields] = useState<Array<string>>(() =>
    mergeOrderedFields(storedLocally?.orderedFields ?? [], defaultOrderedFields),
  )

  const columnVisibilityModelRef = useRef(columnVisibilityModel)
  const pinnedColumnsRef = useRef(pinnedColumns)
  const orderedFieldsRef = useRef(orderedFields)

  useEffect(() => {
    columnVisibilityModelRef.current = columnVisibilityModel
  }, [columnVisibilityModel])

  useEffect(() => {
    pinnedColumnsRef.current = pinnedColumns
  }, [pinnedColumns])

  useEffect(() => {
    orderedFieldsRef.current = orderedFields
  }, [orderedFields])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
      }
    }
  }, [userId])

  useEffect(() => {
    setColumnVisibilityModel((prev) => ({ ...defaultVisibilityModel, ...prev }))
  }, [defaultVisibilityModel])

  useEffect(() => {
    const load = async () => {
      try {
        const response = await createAsyncAction<{ preferences?: ColumnPreferences }, any>(
          dispatch,
          FetchTableConfig.request({ tableKey }),
        )

        if (response?.preferences) {
          const merged = fromApiPreferences(response.preferences, defaultPreferences)
          const withDefaults: GridColumnPreferences = {
            columnVisibilityModel: { ...defaultVisibilityModel, ...merged.columnVisibilityModel },
            pinnedColumns: merged.pinnedColumns,
            orderedFields: mergeOrderedFields(merged.orderedFields, defaultOrderedFields),
          }

          setColumnVisibilityModel(withDefaults.columnVisibilityModel)
          setPinnedColumns(withDefaults.pinnedColumns)
          setOrderedFields(withDefaults.orderedFields)

          if (userId) {
            saveToStorage(userId, tableKey, withDefaults)
          }
        }
      } catch {}
    }

    load()
  }, [tableKey, userId])

  const scheduleSave = useCallback(
    (preferences: GridColumnPreferences) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
      }

      if (userId) {
        saveToStorage(userId, tableKey, preferences)
      }

      saveTimerRef.current = setTimeout(() => {
        dispatch(
          UpsertTableConfig.request({
            tableKey,
            preferences: toApiPreferences(preferences),
          }),
        )
      }, DEBOUNCE_MS)
    },
    [tableKey, userId, dispatch],
  )

  const applyProtectedVisibility = useCallback(
    (model: GridColumnVisibilityModel): GridColumnVisibilityModel => {
      return lockedColumns.reduce<GridColumnVisibilityModel>(
        (result, field) => {
          result[field] = true

          return result
        },
        { ...model },
      )
    },
    [lockedColumns],
  )

  const onColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const protectedModel = applyProtectedVisibility(model)

      setColumnVisibilityModel(protectedModel)
      scheduleSave({
        columnVisibilityModel: protectedModel,
        pinnedColumns: pinnedColumnsRef.current,
        orderedFields: orderedFieldsRef.current,
      })
    },
    [applyProtectedVisibility, scheduleSave],
  )

  const onPinnedColumnsChange = useCallback(
    (pinned: GridPinnedColumns) => {
      setPinnedColumns(pinned)
      scheduleSave({
        columnVisibilityModel: columnVisibilityModelRef.current,
        pinnedColumns: pinned,
        orderedFields: orderedFieldsRef.current,
      })
    },
    [scheduleSave],
  )

  const onColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      const draggedField = params.column.field
      const visibilityModel = columnVisibilityModelRef.current

      const withoutDragged = orderedFieldsRef.current.filter((field) => {
        return field !== draggedField
      })
      const visibleWithoutDragged = withoutDragged.filter((field) => {
        return visibilityModel[field] !== false
      })

      const targetField = visibleWithoutDragged[params.targetIndex]
      const targetIndexInAll = targetField
        ? withoutDragged.indexOf(targetField)
        : withoutDragged.length

      withoutDragged.splice(targetIndexInAll, 0, draggedField)

      setOrderedFields(withoutDragged)
      scheduleSave({
        columnVisibilityModel: columnVisibilityModelRef.current,
        pinnedColumns: pinnedColumnsRef.current,
        orderedFields: withoutDragged,
      })
    },
    [scheduleSave],
  )

  const onToggleColumnVisibility = useCallback(
    (field: string) => {
      if (lockedColumns.includes(field)) {
        return
      }

      const updatedModel = {
        ...columnVisibilityModelRef.current,
        [field]: !columnVisibilityModelRef.current[field],
      }

      setColumnVisibilityModel(updatedModel)
      scheduleSave({
        columnVisibilityModel: updatedModel,
        pinnedColumns: pinnedColumnsRef.current,
        orderedFields: orderedFieldsRef.current,
      })
    },
    [lockedColumns, scheduleSave],
  )

  const onPinColumn = useCallback(
    (field: string) => {
      const updatedPinned: GridPinnedColumns = {
        left: [
          ...(pinnedColumnsRef.current.left ?? []).filter((pinnedField) => {
            return pinnedField !== field
          }),
          field,
        ],
        right: (pinnedColumnsRef.current.right ?? []).filter((pinnedField) => {
          return pinnedField !== field
        }),
      }

      setPinnedColumns(updatedPinned)
      scheduleSave({
        columnVisibilityModel: columnVisibilityModelRef.current,
        pinnedColumns: updatedPinned,
        orderedFields: orderedFieldsRef.current,
      })
    },
    [scheduleSave],
  )

  const onUnpinColumn = useCallback(
    (field: string) => {
      const updatedPinned: GridPinnedColumns = {
        left: (pinnedColumnsRef.current.left ?? []).filter((pinnedField) => {
          return pinnedField !== field
        }),
        right: (pinnedColumnsRef.current.right ?? []).filter((pinnedField) => {
          return pinnedField !== field
        }),
      }

      setPinnedColumns(updatedPinned)
      scheduleSave({
        columnVisibilityModel: columnVisibilityModelRef.current,
        pinnedColumns: updatedPinned,
        orderedFields: orderedFieldsRef.current,
      })
    },
    [scheduleSave],
  )

  const orderedColumns = useMemo(() => {
    if (!orderedFields.length) {
      return columns
    }

    return [...columns].sort((leftColumn, rightColumn) => {
      const leftColumnIndex = orderedFields.indexOf(leftColumn.field)
      const rightColumnIndex = orderedFields.indexOf(rightColumn.field)

      if (leftColumnIndex === -1 && rightColumnIndex === -1) {
        return 0
      }

      if (leftColumnIndex === -1) {
        return 1
      }

      if (rightColumnIndex === -1) {
        return -1
      }

      return leftColumnIndex - rightColumnIndex
    })
  }, [columns, orderedFields])

  return {
    columnVisibilityModel,
    pinnedColumns,
    orderedFields,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  }
}
